import { BrowserModule } from "@angular/platform-browser";
import { NgModule, inject, provideAppInitializer } from "@angular/core";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { TranslateModule, TranslateLoader } from "@ngx-translate/core";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { SchedulerModule } from "@progress/kendo-angular-scheduler";
import { PartialsModule } from "../app/common-layout/layout/partials/partials.module";
import { CoreModule } from "./core/core.module";
import { AuthGuard } from "./core/auth/auth.guard";
import { ExcelModule, GridModule } from "@progress/kendo-angular-grid";
import { MenuModule } from "@progress/kendo-angular-menu";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { SplashScreenComponent } from "./shared/splash-screen/splash-screen.component";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { SplashScreenService } from "./shared/splash-screen/splash-screen.service";
import { LoaderComponent } from "./shared/loader/loader.component";
import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app-routing.module";
import { DateInputsModule } from "@progress/kendo-angular-dateinputs";
import { TooltipModule } from "@progress/kendo-angular-tooltip";
import { LanguageSelectorComponent } from "./common-layout/layout/header/language-selector/language-selector.component";
import { AuthService } from "./core/auth/auth.service";
import { ToastrModule } from "ngx-toastr";
import { AppointmentToastrService } from "./shared/appointment-toastr/appointment-toastr.service";
import { ScrollingModule } from "@angular/cdk/scrolling";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { IPublicClientApplication, PublicClientApplication, InteractionType, BrowserCacheLocation, LogLevel } from '@azure/msal-browser';
import { MsalInterceptor, MsalInterceptorConfiguration, MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { environment } from "../environments/environment";
import { UtilityService } from "./common-layout/service/utility.service";
import { MatDialogModule } from "@angular/material/dialog";
import { ServiceWorkerModule } from '@angular/service-worker';
import { SessionOutModule } from "./session-out/session-out.module";
import { DashboardService } from "./dashboard/dashboard.service";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MenuHorizontalService } from "./common-layout/service/menu-horizontal.service";
import { MenuConfigService } from "./common-layout/service/menu-config.service";
import { FormioModule } from "@formio/angular";
import { RadiologyViewReportComponent } from './radiology-view-report/radiology-view-report.component';
import { ChartsModule } from '@progress/kendo-angular-charts';
import { UrlSerializer } from "@angular/router";
import { LowerCaseUrlSerializer } from "./core/routing/lower-case-url-serializer";

export function httpTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

export function startupServiceFactory(authService: AuthService): Function {
  return () => authService.initSession();
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.clientId,
      authority: 'https://login.microsoftonline.com/common',
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      allowNativeBroker: false,
      loggerOptions: {
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      }
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read']
    },
    loginFailedRoute: '/login-failed'
  };
}

@NgModule({ declarations: [
        AppComponent,
        SplashScreenComponent,
        LoaderComponent,
        LanguageSelectorComponent,
        RadiologyViewReportComponent,
    ],
    bootstrap: [AppComponent], imports: [FormioModule,
        BrowserModule,
        MsalModule,
        BrowserAnimationsModule,
        NgbModule,
        CoreModule,
        AppRoutingModule,
        MatProgressSpinnerModule,
        PartialsModule,
        GridModule,
        ExcelModule,
        MatButtonToggleModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: httpTranslateLoader,
                deps: [HttpClient],
            },
        }),
        SchedulerModule,
        MenuModule,
        DateInputsModule,
        TooltipModule,
        ScrollingModule,
        ChartsModule,
        ToastrModule.forRoot({
            newestOnTop: false,
            enableHtml: true,
        }),
        MatTooltipModule,
        MatDialogModule,
        SessionOutModule,
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: environment.production,
            // Register the ServiceWorker as soon as the application is stable
            // or after 30 seconds (whichever comes first).
            registrationStrategy: 'registerWhenStable:30000'
        })], providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory
        },
        MsalService,
        SplashScreenService,
        UtilityService,
        AuthGuard,
        provideAppInitializer(() => {
        const initializerFn = (startupServiceFactory)(inject(AuthService));
        return initializerFn();
      }),
        AppointmentToastrService,
        DashboardService,
        MenuHorizontalService,
        MenuConfigService,
        {
            provide: UrlSerializer,
            useClass: LowerCaseUrlSerializer,
        },
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule { }
